import type {
  QuestionCommentModelData,
  QuestionTextModelData,
  SurveyAnswerRecord,
  SurveyAnswerValue,
  SurveyElementJson,
} from '@coorum-research/shared-common/types'
import type { SurveyModel } from 'survey-core'
import type { SurveyElementFormHandler } from './survey-element-form'
import { validateTextInput, ValidationErrorMessages } from '@coorum-research/shared-common/shared/survey/survey-validation'

export function isTextOrCommentElement(element: SurveyElementJson): element is QuestionTextModelData | QuestionCommentModelData {
  return element.type === 'text' || element.type === 'comment'
}

export function validateTextOrCommentElement(
  element: QuestionTextModelData | QuestionCommentModelData,
  value: string,
): {
  isValid: boolean
  errorMessage?: string
} {
  // 必須チェック
  if (element.isRequired && (value === null || value === undefined || value === '')) {
    return {
      isValid: false,
      errorMessage: ValidationErrorMessages.REQUIRED,
    }
  }

  // validationTypeが設定されていない場合はバリデーションをスキップ
  if (!element.validationType) {
    return {
      isValid: true,
    }
  }

  if (value !== '') {
    const errors = validateTextInput(
      value,
      element.type,
      element.validationType,
    )
    if (errors.length > 0) {
      return {
        isValid: false,
        errorMessage: errors[0].getText(),
      }
    }
  }

  return {
    isValid: true,
  }
}

export class SurveyElementFormHandlerText implements SurveyElementFormHandler {
  readonly #elementJson: SurveyElementJson
  #elementResultRecord: SurveyAnswerRecord | null

  constructor(
    elementJson: SurveyElementJson,
    elementResultRecord: SurveyAnswerRecord | null,
  ) {
    this.#elementJson = elementJson
    this.#elementResultRecord = elementResultRecord
  }

  get elementJson(): SurveyElementJson {
    return this.#elementJson
  }

  get surveyModel(): SurveyModel | null {
    return null
  }

  get data(): SurveyAnswerRecord {
    return this.#elementResultRecord || {}
  }

  getValue(): SurveyAnswerValue {
    return this.#elementResultRecord?.[this.#elementJson.name] ?? ''
  }

  getComment(): string {
    return this.#elementResultRecord?.[`${this.#elementJson.name}-Comment`] as string || ''
  }

  setElementResultRecordValue(value: SurveyAnswerValue) {
    if (!this.#elementResultRecord) {
      this.#elementResultRecord = {}
    }

    this.#elementResultRecord[this.#elementJson.name] = value
  }

  setElementResultRecordComment(_comment: string) {
    // nothing to do
  }

  validate(): {
    isValid: boolean
    errorMessage?: string
  } {
    const value = this.getValue()

    if (!isTextOrCommentElement(this.#elementJson)) {
      return { isValid: true }
    }

    return validateTextOrCommentElement(this.#elementJson, value as string)
  }
}
